import React, { startTransition, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { iconType } from '../../utils';
import TableLayout from '../../components/TableLayout';
import { useAuth } from '../../App';

const Container = styled.div`
  padding: 20px;
  background-color: #F9F6F2;
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow: hidden;
`;

const Title = styled.h1`
  color: #4A3C31;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const TableWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
  width: 400px;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    width: 350px;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 400px;
  }

  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-left: 2.5rem;
  border: 1px solid #E8D5B5;
  border-radius: 4px;
  font-size: 1rem;
  color: #2C1810;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #8C7355;
  }
  
  &::placeholder {
    color: #8C7355;
  }

  @media (max-width: 768px) {
    padding: 0.8rem;
    padding-left: 2.5rem;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 0.85rem;
    padding-left: 2.5rem;
    font-size: 0.9rem;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #8C7355;

  @media (max-width: 768px) {
    left: 0.9rem;
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    left: 0.8rem;
    font-size: 0.85rem;
  }
`;

const StoreSearchBar = ({ value, onChange, placeholder = 'Search...' }) => {
  return (
    <SearchContainer>
      <SearchIcon>
        <FontAwesomeIcon icon={faSearch} />
      </SearchIcon>
      <SearchInput
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
    </SearchContainer>
  );
};

const formatPhoneNumber = (phoneNumber) => {
  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Check if we have a valid 10-digit number
  if (cleaned.length === 10) {
    // Format as (xxx)xxx-xxxx
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  // Return original if not a valid 10-digit number
  return phoneNumber;
};

const StoreSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, stores: contextStores } = useAuth();
  const stores = useMemo(() => {
    if (Array.isArray(contextStores) && contextStores.length > 0) return contextStores;
    if (user?.stores && Array.isArray(user.stores)) return user.stores;
    return [];
  }, [contextStores, user]);

  // Track if component is mounted
  const isMounted = React.useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Use stores from location state or context
  const storeList = useMemo(() => {
    return location.state?.stores || stores || [];
  }, [location.state?.stores, stores]);

  const [sortField, setSortField] = React.useState('name');
  const [sortDirection, setSortDirection] = React.useState('asc');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedStoreId, setSelectedStoreId] = React.useState(null);

  // Filter stores based on search term
  const filteredStores = useMemo(() => {
    if (!searchTerm.trim()) return storeList;
    
    const term = searchTerm.toLowerCase().trim();
    return storeList.filter(store => 
      store.name?.toLowerCase().includes(term) ||
      store.address1?.toLowerCase().includes(term) ||
      store.city?.toLowerCase().includes(term) ||
      store.state?.toLowerCase().includes(term) ||
      store.phone?.includes(term)
    );
  }, [storeList, searchTerm]);

  // Sort stores when sort field, direction, or filtered stores change
  const sortedStores = useMemo(() => {
    if (!Array.isArray(filteredStores)) return [];
    return [...filteredStores].sort((a, b) => {
      const aValue = a[sortField]?.toString().toLowerCase() || '';
      const bValue = b[sortField]?.toString().toLowerCase() || '';
      const modifier = sortDirection === 'asc' ? 1 : -1;
      
      if (aValue < bValue) return -1 * modifier;
      if (aValue > bValue) return 1 * modifier;
      return 0;
    });
  }, [filteredStores, sortField, sortDirection]);

  // Pagination logic
  const pageSize = 10;
  const totalPages = Math.ceil(sortedStores.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedStores = useMemo(() => {
    return sortedStores.slice(startIndex, endIndex);
  }, [sortedStores, startIndex, endIndex]);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Reset to first page when sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortField, sortDirection]);

  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleRowClick = (store) => {
    setSelectedStoreId(store.customer_id);
    localStorage.setItem('selectedStoreId', store.customer_id);
    startTransition(() => navigate(`/store/${store.customer_id}?market=${store.market}`, { state: { store } }));
  };

  const columns = [
    {
      field: 'name',
      label: 'Store Name',
      sortable: true
    },
    {
      field: 'address1',
      label: 'Address',
      sortable: true,
      render: (row) => (
        <div>
          <div>{row.address1}</div>
          {row.address2 && <div>{row.address2}</div>}
          <div>{`${row.city}, ${row.state} ${row.zip_code}`}</div>
        </div>
      )
    },
    {
      field: 'phone',
      label: 'Phone',
      sortable: true,
      render: (row) => formatPhoneNumber(row.phone)
    }
  ];

  console.log('Render state:', { isLoading: false, storesCount: storeList.length });

  if (false) { // Errors now handled by AppWithAuth
    return (
      <Container>
        <Title>
          <FontAwesomeIcon icon={iconType.Store} />
          Error Loading Stores
        </Title>
        <div>Failed to load stores. Please try again later.</div>
      </Container>
    );
  }

  return (
    <Container>
      <Title>
        <FontAwesomeIcon icon={iconType.Store} />
        Select Your Store
      </Title>
      {storeList.length > 0 ? (
        <TableContainer>
          <StoreSearchBar
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search stores by name, address, city, state, or phone..."
          />
          <TableWrapper>
            <TableLayout
              columns={columns}
              data={paginatedStores}
              loading={false}
              onRowClick={handleRowClick}
              onSort={handleSort}
              sortField={sortField}
              sortDirection={sortDirection}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              totalPages={totalPages}
              selectedRow={selectedStoreId}
            />
          </TableWrapper>
        </TableContainer>
      ) : (
        <div>No stores available.</div>
      )}
    </Container>
  );
};

export default StoreSelection;
